import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MultiLanguageInput } from "@/components/admin/MultiLanguageInput";
import { MultiLanguageTextarea } from "@/components/admin/MultiLanguageTextarea";

const AdminTours = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingTour, setEditingTour] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<"es" | "en" | "fr">("es");
  const [uploadingImages, setUploadingImages] = useState(false);

  const { data: tours, isLoading } = useQuery({
    queryKey: ["admin-tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tours").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tours"] });
      toast({ title: "Tour eliminado correctamente" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (tour: any) => {
      // Calculate duration_days from itinerary length
      const tourToSave = {
        ...tour,
        duration_days: tour.itinerary?.length || 1
      };
      
      if (tour.id) {
        const { error } = await supabase
          .from("tours")
          .update(tourToSave)
          .eq("id", tour.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("tours").insert([tourToSave]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tours"] });
      toast({ title: "Tour guardado correctamente" });
      setIsEditing(false);
      setEditingTour(null);
    },
  });

  const handleEdit = (tour: any) => {
    setEditingTour(tour);
    setIsEditing(true);
  };

  const handleNew = () => {
    setEditingTour({
      name: "",
      name_en: "",
      name_fr: "",
      slug: "",
      short_description: "",
      short_description_en: "",
      short_description_fr: "",
      full_description: "",
      full_description_en: "",
      full_description_fr: "",
      destination: "",
      destination_en: "",
      destination_fr: "",
      price_per_person: 0,
      duration_days: 1,
      image_url: "",
      gallery_images: [],
      included_services: [],
      included_services_en: [],
      included_services_fr: [],
      excluded_services: [],
      excluded_services_en: [],
      excluded_services_fr: [],
      itinerary: [],
      min_group_size: 2,
      max_group_size: 12,
      is_active: true,
      is_featured: false,
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of Array.from(files)) {
        // Validate file size (5MB max)
        if (file.size > 5242880) {
          toast({ 
            title: "Error", 
            description: `${file.name} excede el tamaño máximo de 5MB`,
            variant: "destructive"
          });
          continue;
        }

        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('tour-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('tour-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Update gallery_images array
      const currentImages = editingTour.gallery_images || [];
      setEditingTour({
        ...editingTour,
        gallery_images: [...currentImages, ...uploadedUrls],
        // Set first image as image_url if not set
        image_url: editingTour.image_url || uploadedUrls[0] || ""
      });

      toast({ title: `${uploadedUrls.length} imagen(es) subida(s) correctamente` });
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({ 
        title: "Error al subir imágenes", 
        description: "Por favor intenta de nuevo",
        variant: "destructive"
      });
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const updatedImages = editingTour.gallery_images.filter(
      (_: string, index: number) => index !== indexToRemove
    );
    setEditingTour({
      ...editingTour,
      gallery_images: updatedImages
    });
  };

  if (isEditing && editingTour) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editingTour.id ? "Editar Tour" : "Nuevo Tour"}</CardTitle>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={selectedLanguage === "es" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedLanguage("es")}
              >
                ES
              </Button>
              <Button
                type="button"
                variant={selectedLanguage === "en" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedLanguage("en")}
              >
                EN
              </Button>
              <Button
                type="button"
                variant={selectedLanguage === "fr" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedLanguage("fr")}
              >
                FR
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={
                  selectedLanguage === "es"
                    ? editingTour.name || ""
                    : selectedLanguage === "en"
                    ? editingTour.name_en || ""
                    : editingTour.name_fr || ""
                }
                onChange={(e) =>
                  setEditingTour({
                    ...editingTour,
                    [selectedLanguage === "es" ? "name" : selectedLanguage === "en" ? "name_en" : "name_fr"]:
                      e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={editingTour.slug}
                onChange={(e) => setEditingTour({ ...editingTour, slug: e.target.value })}
              />
            </div>
            <div>
              <Label>Destino</Label>
              <Input
                value={
                  selectedLanguage === "es"
                    ? editingTour.destination || ""
                    : selectedLanguage === "en"
                    ? editingTour.destination_en || ""
                    : editingTour.destination_fr || ""
                }
                onChange={(e) =>
                  setEditingTour({
                    ...editingTour,
                    [selectedLanguage === "es"
                      ? "destination"
                      : selectedLanguage === "en"
                      ? "destination_en"
                      : "destination_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio por persona ($)</Label>
                <Input
                  type="number"
                  value={editingTour.price_per_person}
                  onChange={(e) => setEditingTour({ ...editingTour, price_per_person: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Duración (días) - Calculado automáticamente</Label>
                <Input
                  type="number"
                  value={editingTour.itinerary?.length || 0}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  La duración se calcula según los días del itinerario
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cantidad mínima de participantes</Label>
                <Input
                  type="number"
                  min="1"
                  value={editingTour.min_group_size || 2}
                  onChange={(e) => {
                    const minSize = parseInt(e.target.value);
                    setEditingTour({ ...editingTour, min_group_size: minSize });
                  }}
                />
              </div>
              <div>
                <Label>Cantidad máxima de participantes</Label>
                <Input
                  type="number"
                  min={editingTour.min_group_size || 1}
                  value={editingTour.max_group_size || 12}
                  onChange={(e) => {
                    const maxSize = parseInt(e.target.value);
                    setEditingTour({ ...editingTour, max_group_size: maxSize });
                  }}
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label>Galería de imágenes</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="tour-image-upload"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('tour-image-upload')?.click()}
                  disabled={uploadingImages}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploadingImages ? "Subiendo..." : "Subir imágenes"}
                </Button>
                <span className="text-sm text-muted-foreground">
                  Máx. 5MB por imagen (JPEG, PNG, WebP)
                </span>
              </div>
              
              {editingTour.gallery_images && editingTour.gallery_images.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {editingTour.gallery_images.map((imageUrl: string, index: number) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-32 object-cover rounded-md"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      {index === 0 && (
                        <Badge className="absolute bottom-2 left-2 bg-primary">
                          Principal
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                La primera imagen se usará como imagen principal del tour
              </p>
            </div>
            <div>
              <Label>Descripción corta</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
                value={
                  selectedLanguage === "es"
                    ? editingTour.short_description || ""
                    : selectedLanguage === "en"
                    ? editingTour.short_description_en || ""
                    : editingTour.short_description_fr || ""
                }
                onChange={(e) =>
                  setEditingTour({
                    ...editingTour,
                    [selectedLanguage === "es"
                      ? "short_description"
                      : selectedLanguage === "en"
                      ? "short_description_en"
                      : "short_description_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Descripción completa</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={8}
                value={
                  selectedLanguage === "es"
                    ? editingTour.full_description || ""
                    : selectedLanguage === "en"
                    ? editingTour.full_description_en || ""
                    : editingTour.full_description_fr || ""
                }
                onChange={(e) =>
                  setEditingTour({
                    ...editingTour,
                    [selectedLanguage === "es"
                      ? "full_description"
                      : selectedLanguage === "en"
                      ? "full_description_en"
                      : "full_description_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Itinerario del Tour</Label>
              <div className="space-y-4 border rounded-lg p-4 bg-secondary/10">
                {editingTour.itinerary && editingTour.itinerary.length > 0 ? (
                  editingTour.itinerary.map((day: any, index: number) => (
                    <div key={index} className="space-y-2 border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Día {day.day}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newItinerary = editingTour.itinerary.filter((_: any, i: number) => i !== index);
                            // Renumber days
                            const renumberedItinerary = newItinerary.map((d: any, i: number) => ({
                              ...d,
                              day: i + 1
                            }));
                            setEditingTour({ ...editingTour, itinerary: renumberedItinerary });
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <textarea
                        className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        rows={2}
                        value={
                          selectedLanguage === "es"
                            ? day.description_es || ""
                            : selectedLanguage === "en"
                            ? day.description_en || ""
                            : day.description_fr || ""
                        }
                        onChange={(e) => {
                          const newItinerary = [...editingTour.itinerary];
                          const field = selectedLanguage === "es" ? "description_es" : selectedLanguage === "en" ? "description_en" : "description_fr";
                          newItinerary[index] = {
                            ...newItinerary[index],
                            [field]: e.target.value
                          };
                          setEditingTour({ ...editingTour, itinerary: newItinerary });
                        }}
                        placeholder={`Descripción de las actividades del día ${day.day}...`}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay días agregados. Agrega el primer día para comenzar el itinerario.
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    const currentItinerary = editingTour.itinerary || [];
                    const newDay = {
                      day: currentItinerary.length + 1,
                      description_es: "",
                      description_en: "",
                      description_fr: ""
                    };
                    setEditingTour({
                      ...editingTour,
                      itinerary: [...currentItinerary, newDay]
                    });
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Día {(editingTour.itinerary?.length || 0) + 1}
                </Button>
              </div>
            </div>
            <div>
              <Label>Qué incluye</Label>
              <div className="space-y-2">
                {(selectedLanguage === "es"
                  ? editingTour.included_services || []
                  : selectedLanguage === "en"
                  ? editingTour.included_services_en || []
                  : editingTour.included_services_fr || []
                ).map((service: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={service}
                      onChange={(e) => {
                        const field = selectedLanguage === "es" ? "included_services" : selectedLanguage === "en" ? "included_services_en" : "included_services_fr";
                        const newServices = [...(editingTour[field] || [])];
                        newServices[index] = e.target.value;
                        setEditingTour({ ...editingTour, [field]: newServices });
                      }}
                      placeholder="Ej: Alojamiento en lodges"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        const field = selectedLanguage === "es" ? "included_services" : selectedLanguage === "en" ? "included_services_en" : "included_services_fr";
                        const newServices = (editingTour[field] || []).filter((_: string, i: number) => i !== index);
                        setEditingTour({ ...editingTour, [field]: newServices });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const field = selectedLanguage === "es" ? "included_services" : selectedLanguage === "en" ? "included_services_en" : "included_services_fr";
                    setEditingTour({
                      ...editingTour,
                      [field]: [...(editingTour[field] || []), ""],
                    });
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar servicio incluido
                </Button>
              </div>
            </div>
            <div>
              <Label>No incluye</Label>
              <div className="space-y-2">
                {(selectedLanguage === "es"
                  ? editingTour.excluded_services || []
                  : selectedLanguage === "en"
                  ? editingTour.excluded_services_en || []
                  : editingTour.excluded_services_fr || []
                ).map((service: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={service}
                      onChange={(e) => {
                        const field = selectedLanguage === "es" ? "excluded_services" : selectedLanguage === "en" ? "excluded_services_en" : "excluded_services_fr";
                        const newServices = [...(editingTour[field] || [])];
                        newServices[index] = e.target.value;
                        setEditingTour({ ...editingTour, [field]: newServices });
                      }}
                      placeholder="Ej: Vuelos internacionales"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        const field = selectedLanguage === "es" ? "excluded_services" : selectedLanguage === "en" ? "excluded_services_en" : "excluded_services_fr";
                        const newServices = (editingTour[field] || []).filter((_: string, i: number) => i !== index);
                        setEditingTour({ ...editingTour, [field]: newServices });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const field = selectedLanguage === "es" ? "excluded_services" : selectedLanguage === "en" ? "excluded_services_en" : "excluded_services_fr";
                    setEditingTour({
                      ...editingTour,
                      [field]: [...(editingTour[field] || []), ""],
                    });
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar servicio no incluido
                </Button>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTour.is_active}
                  onChange={(e) => setEditingTour({ ...editingTour, is_active: e.target.checked })}
                />
                <Label>Activo</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTour.is_featured}
                  onChange={(e) => setEditingTour({ ...editingTour, is_featured: e.target.checked })}
                />
                <Label>Destacado</Label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveMutation.mutate(editingTour)}>Guardar</Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Tours</h1>
        <Button onClick={handleNew}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Tour
        </Button>
      </div>

      {isLoading ? (
        <div>Cargando...</div>
      ) : (
        <div className="grid gap-4">
          {tours?.map((tour) => (
            <Card key={tour.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">{tour.name}</h3>
                      {tour.is_featured && <Badge>Destacado</Badge>}
                      {!tour.is_active && <Badge variant="secondary">Inactivo</Badge>}
                    </div>
                    <p className="text-muted-foreground mb-2">{tour.short_description}</p>
                    <div className="text-sm text-muted-foreground">
                      {tour.destination} • {tour.duration_days} días • ${tour.price_per_person}/persona
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => handleEdit(tour)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => deleteMutation.mutate(tour.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTours;
